import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import type { Achievement } from '../../types/buildExpansion';
import AchievementBadge, { formatRankContext, formatScopeLabel } from './AchievementBadge';

/**
 * Groups a build's recognition with strict class separation: platform
 * achievements (Bilt calculated) and milestones never share a section.
 * Historical entries collapse behind a disclosure. Selecting a badge opens
 * an inline detail panel.
 */

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const humanizeToken = (token: string): string => token.replace(/[_-]+/g, ' ').trim();

interface AchievementDetailProps {
  achievement: Achievement;
  onClose: () => void;
}

const AchievementDetail: React.FC<AchievementDetailProps> = ({ achievement, onClose }) => {
  const name = achievement.name || achievement.short_name || 'Achievement';
  const rankContext =
    achievement.achievement_class === 'platform_achievement' ? formatRankContext(achievement) : null;
  const scopeLabel = formatScopeLabel(achievement.scope);
  const issued = formatDate(achievement.issued_at);
  const valueSnapshot =
    achievement.value != null
      ? `${achievement.value.toLocaleString()}${achievement.metric ? ` ${humanizeToken(achievement.metric)}` : ''}`
      : null;

  return (
    <div
      role="region"
      aria-label={`${name} details`}
      className="rounded-xl border border-white/[0.08] bg-[#0a0a0a]/80 p-4 mt-3"
    >
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-sm font-semibold text-white">{name}</h4>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close achievement details"
          className="btn-icon shrink-0 -mr-1 -mt-1 cursor-pointer"
        >
          <XMarkIcon className="w-4 h-4" aria-hidden />
        </button>
      </div>

      {achievement.description && (
        <p className="text-sm text-gray-400 mt-1.5 leading-relaxed">{achievement.description}</p>
      )}

      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rankContext && (
          <div>
            <dt className="text-meta">Standing</dt>
            <dd className="text-gray-200 mt-0.5">{rankContext}</dd>
          </div>
        )}
        {valueSnapshot && (
          <div>
            <dt className="text-meta">Value at snapshot</dt>
            <dd className="text-gray-200 mt-0.5 tabular-nums">{valueSnapshot}</dd>
          </div>
        )}
        {issued && (
          <div>
            <dt className="text-meta">Issued</dt>
            <dd className="text-gray-200 mt-0.5">{issued}</dd>
          </div>
        )}
        {scopeLabel && (
          <div>
            <dt className="text-meta">Scope</dt>
            <dd className="text-gray-200 mt-0.5">{scopeLabel}</dd>
          </div>
        )}
      </dl>
    </div>
  );
};

interface BadgeGroupProps {
  heading: string;
  description?: string;
  achievements: Achievement[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onCloseDetail: () => void;
}

const BadgeGroup: React.FC<BadgeGroupProps> = ({
  heading,
  description,
  achievements,
  selectedId,
  onSelect,
  onCloseDetail,
}) => {
  if (achievements.length === 0) return null;
  const selected = achievements.find((a) => a.id === selectedId) || null;

  return (
    <div>
      <div className="flex items-baseline gap-3">
        <h3 className="font-display text-base font-semibold tracking-tight text-white">{heading}</h3>
        <span className="font-mono text-[11px] text-gray-600 tabular-nums" aria-hidden>
          {String(achievements.length).padStart(2, '0')}
        </span>
      </div>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3 list-none">
        {achievements.map((achievement) => (
          <li key={achievement.id}>
            <AchievementBadge
              achievement={achievement}
              expanded={achievement.id === selectedId}
              onClick={() =>
                achievement.id === selectedId ? onCloseDetail() : onSelect(achievement.id)
              }
            />
          </li>
        ))}
      </ul>
      {selected && <AchievementDetail achievement={selected} onClose={onCloseDetail} />}
    </div>
  );
};

export interface AchievementCollectionProps {
  achievements: Achievement[];
  className?: string;
}

export const AchievementCollection: React.FC<AchievementCollectionProps> = ({
  achievements,
  className = '',
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [historicalOpen, setHistoricalOpen] = useState(false);

  const groups = useMemo(() => {
    const current = achievements.filter((a) => a.status === 'current' || a.status == null);
    const historical = achievements.filter((a) => a.status != null && a.status !== 'current');
    return {
      platform: current.filter((a) => a.achievement_class === 'platform_achievement'),
      credentials: current.filter((a) => a.achievement_class === 'verified_credential'),
      milestones: current.filter((a) => a.achievement_class === 'build_milestone'),
      other: current.filter((a) => a.achievement_class == null),
      historical,
    };
  }, [achievements]);

  if (achievements.length === 0) return null;

  const closeDetail = () => setSelectedId(null);

  return (
    <section
      className={`border border-white/[0.06] bg-[#0c0c0c]/80 p-5 sm:p-8 space-y-8 ${className}`}
      aria-label="Build recognition"
    >
      <div className="border-b border-white/[0.06] pb-5">
        <p className="kicker mb-2">Achievements</p>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
          What this build has earned
        </h2>
        <p className="text-sm text-gray-500 mt-2 max-w-[48ch] leading-relaxed">
          Platform standings, verified credentials, and milestones from what is documented here.
        </p>
      </div>
      {/* Achievements + milestones share a row on desktop to cut vertical length. */}
      {(groups.platform.length > 0 || groups.milestones.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 md:items-start">
          <BadgeGroup
            heading="Bilt achievements"
            description="Earned from what’s documented on this build."
            achievements={groups.platform}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCloseDetail={closeDetail}
          />
          <BadgeGroup
            heading="Milestones"
            description="Documented moments in this build's history."
            achievements={groups.milestones}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCloseDetail={closeDetail}
          />
        </div>
      )}
      <BadgeGroup
        heading="Verified credentials"
        achievements={groups.credentials}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onCloseDetail={closeDetail}
      />
      <BadgeGroup
        heading="Other recognition"
        achievements={groups.other}
        selectedId={selectedId}
        onSelect={setSelectedId}
        onCloseDetail={closeDetail}
      />

      {groups.historical.length > 0 && (
        <div className="border-t border-white/[0.06] pt-4">
          <button
            type="button"
            onClick={() => setHistoricalOpen((open) => !open)}
            aria-expanded={historicalOpen}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400 rounded min-h-[44px] md:min-h-[40px]"
          >
            <ChevronDownIcon
              className={`w-4 h-4 transition-transform duration-200 motion-reduce:transition-none ${
                historicalOpen ? 'rotate-180' : ''
              }`}
              aria-hidden
            />
            Historical ({groups.historical.length})
          </button>
          {historicalOpen && (
            <div className="mt-1">
              <p className="text-xs text-gray-500">
                Recognition this build previously held. Standings change as the platform grows.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3 list-none">
                {groups.historical.map((achievement) => (
                  <li key={achievement.id}>
                    <AchievementBadge
                      achievement={achievement}
                      expanded={achievement.id === selectedId}
                      onClick={() =>
                        achievement.id === selectedId ? closeDetail() : setSelectedId(achievement.id)
                      }
                    />
                  </li>
                ))}
              </ul>
              {(() => {
                const selected = groups.historical.find((a) => a.id === selectedId) || null;
                return selected ? (
                  <AchievementDetail achievement={selected} onClose={closeDetail} />
                ) : null;
              })()}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default AchievementCollection;
